"use client";

import {
  CloseButton,
  Field,
  FileUpload,
  Input,
  InputGroup,
  Text,
} from "@chakra-ui/react";
import type { FileChangeDetails } from "@zag-js/file-upload";
import type { ReactNode } from "react";
import { LuFileUp } from "react-icons/lu";

export interface GenericUploadFileProps {
  accept?: string;
  disabled?: boolean;
  fileInputKey?: number | string;
  helperText?: ReactNode;
  label?: string;
  maxFiles?: number;
  onClear: () => void;
  onFileChange: (details: FileChangeDetails) => void;
  selectedFile: File | null;
  selectedFileLabel?: string;
  validationMessage?: string | null;
}

function formatFileSize(fileSizeBytes: number): string {
  const sizeInKb = Math.max(1, Math.round(fileSizeBytes / 1024));

  if (sizeInKb < 1024) {
    return `${sizeInKb} KB`;
  }

  return `${(fileSizeBytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function GenericUploadFile({
  accept,
  disabled = false,
  fileInputKey,
  helperText,
  label,
  maxFiles = 1,
  onClear,
  onFileChange,
  selectedFile,
  selectedFileLabel = "Selected",
  validationMessage,
}: GenericUploadFileProps) {
  return (
    <Field.Root invalid={!!validationMessage}>
      {label && <Field.Label>{label}</Field.Label>}

      <FileUpload.Root
        key={fileInputKey}
        accept={accept}
        disabled={disabled}
        gap="1"
        maxFiles={maxFiles}
        maxWidth="full"
        onFileChange={onFileChange}
      >
        <FileUpload.HiddenInput />

        <InputGroup
          w="full"
          startElement={<LuFileUp />}
          endElement={
            selectedFile ? (
              <FileUpload.ClearTrigger asChild>
                <CloseButton
                  disabled={disabled}
                  size="xs"
                  variant="plain"
                  onClick={onClear}
                />
              </FileUpload.ClearTrigger>
            ) : undefined
          }
        >
          <Input asChild>
            <FileUpload.Trigger>
              <FileUpload.FileText lineClamp={1} />
            </FileUpload.Trigger>
          </Input>
        </InputGroup>
      </FileUpload.Root>

      {selectedFile && (
        <Text
          mt="2"
          fontSize="sm"
          color="moveon.text.muted"
          wordBreak="break-word"
        >
          {selectedFileLabel}: {selectedFile.name} (
          {formatFileSize(selectedFile.size)})
        </Text>
      )}

      {validationMessage ? (
        <Field.ErrorText>{validationMessage}</Field.ErrorText>
      ) : (
        helperText && <Field.HelperText>{helperText}</Field.HelperText>
      )}
    </Field.Root>
  );
}
